// OS MOTORES DE CANVAS.
//
// O formato de documento v2 declara que cada página tem um `motor` — com que tecnologia ela se
// desenha. Declarar não é desenhar: enquanto só um motor existisse, o campo seria promessa.
// Este arquivo é o que torna o campo real.
//
// ===========================================================================
// Por que mais de um motor, e não um canvas que faz tudo
// ===========================================================================
//
// Pesquisa feita em 2026-09-09 sobre as bibliotecas de canvas em uso hoje (Konva, Fabric.js,
// tldraw, Excalidraw). O resumo que importa para uma plataforma escolar:
//
//   - Canvas (Konva/Fabric) é imbatível para MANIPULAR: arrastar, girar, redimensionar, camadas.
//     E é péssimo para LER: o texto não é selecionável, não é lido por leitor de tela, não é
//     encontrado por busca, e não se amplia com o zoom do navegador.
//
//   - HTML/CSS é o contrário: o texto é texto de verdade — copiável, acessível, buscável,
//     imprimível — e a manipulação direta custa mais para construir.
//
// Numa plataforma onde a criança CRIA e depois a turma inteira LÊ, os dois momentos têm
// necessidades opostas. É por isso que o motor é uma propriedade da página e não uma escolha
// global do produto: a página de edição pode ser Konva enquanto a mesma história, na biblioteca,
// é desenhada em HTML e fica legível para quem usa leitor de tela.
//
// Os quatro motores:
//
//   `grade-css`      — quadros em grade CSS. Texto real, acessível, imprimível. Tirinha e prancha.
//   `fluxo-vertical` — rolagem contínua, um quadro por tela. É como criança lê no celular.
//   `svg`            — vetor, escala sem perder nitidez. Capa e exportação.
//   `konva`          — o canvas de manipulação que a plataforma já usa para EDITAR.
//
// Este módulo desenha os três primeiros. O `konva` cai no `grade-css` para LEITURA, de propósito
// e declaradamente: o editor Konva existe e é onde se edita; para ler, o HTML é melhor. Fingir
// que este módulo desenha Konva seria pior que dizer que ele não desenha.

import { useEffect, useRef, useState } from "react";
import * as React from "react";
import { DocumentoHQ, Pagina,
  OPACIDADE_RETICULA,
  Reticula,
  TRAMA,
} from "../api/documento.types";
import { DesenharElemento } from "./elementos";
import "./motores.scss";

/** Todos os elementos de uma página, em ordem de camada. */
function ElementosDaPagina({ pagina }: { pagina: Pagina }) {
  return (
    <>
      {pagina.camadas.map((camada) =>
        camada.visivel === false ? null : (
          <div
            key={camada.id}
            className="motor-camada"
            style={{ opacity: camada.opacidade ?? 1 }}
            data-camada={camada.nome}
          >
            {camada.elementos.map((el) => (
              <DesenharElemento key={el.id} el={el} larguraDaPagina={pagina.largura} />
            ))}
          </div>
        )
      )}
    </>
  );
}

/**
 * A RETICULA DA PAGINA — T2.108, que ele chamou de "essencial".
 *
 * "aplicar reticula em todas as paginas, se quiser, para tentar criar uma UNIFORMIDADE dos
 *  desenhos, do traco."
 *
 * ── POR QUE UMA CAMADA E NAO UM `background-image` NA FOLHA ────────────────────────────────
 *
 * A folha ja usa `background` para `corFundo`, e um `background-image` por cima disputaria a
 * mesma propriedade. Pior: a reticula precisa ficar ACIMA dos elementos, e nao atras — e o que
 * ela faz e amarrar desenhos de tratamentos diferentes, o que so funciona passando por cima
 * deles. Atras, ela seria papel de parede e nao trama.
 *
 * ── O PADRAO E O MESMO DA MARCA ───────────────────────────────────────────────────────────
 *
 * `radial-gradient` de pontos a cada 9px, identico ao `.hq-trama` do `tokens.scss`, que por sua
 * vez veio do material de marca (a capa oficial usa halftone — ver `CATALOGO-SOCIAL-MEDIA.md`).
 * Duplico os valores aqui em vez de reusar a classe por um motivo: `.hq-trama` tem a opacidade
 * amarrada ao TEMA (`--hq-trama: 0` no classico, para a trama sumir lá). Aqui a opacidade e
 * ESCOLHA DE QUEM MONTA, e nao consequencia do tema — sao duas coisas com o mesmo desenho e
 * donos diferentes.
 *
 * `pointer-events: none` porque ela nao e alvo de nada: e acabamento, e um clique nela tem que
 * atravessar para o que esta embaixo.
 */
function ReticulaDaPagina({ reticula }: { reticula?: Reticula }) {
  const opacidade = OPACIDADE_RETICULA[reticula ?? "nenhuma"];
  if (!opacidade) return null;
  return (
    <div
      className="motor-reticula"
      aria-hidden="true"
      style={{
        position: "absolute",
        inset: 0,
        pointerEvents: "none",
        opacity: opacidade,
        // Os numeros vem de `TRAMA`, e nao escritos aqui: o motor SVG desenha a MESMA trama por
        // outro caminho (`<pattern>`), e com os valores em dois lugares eles divergiriam no
        // primeiro ajuste — sem ninguem notar ate comparar duas paginas lado a lado.
        backgroundImage: `radial-gradient(var(--hq-halftone) ${TRAMA.RAIO}px, transparent ${
          TRAMA.RAIO + 0.1
        }px)`,
        backgroundSize: `${TRAMA.PASSO}px ${TRAMA.PASSO}px`,
      }}
    />
  );
}

/**
 * Motor de grade CSS — a folha em proporção fixa, com os elementos posicionados.
 *
 * A folha escala por `transform`, e não redesenhando em outro tamanho: as coordenadas do
 * documento continuam sendo as do documento, em qualquer largura de tela. Sem isso, cada
 * tamanho de tela precisaria de um cálculo próprio e as posições se perderiam.
 */
function MotorGradeCss({ pagina, escala }: { pagina: Pagina; escala: number }) {
  return (
    <div
      className="motor-folha"
      style={{
        width: pagina.largura,
        height: pagina.altura,
        background: pagina.corFundo ?? "var(--hq-superficie)",
        transform: `scale(${escala})`,
        transformOrigin: "top left",
      }}
    >
      <ElementosDaPagina pagina={pagina} />
      <ReticulaDaPagina reticula={pagina.reticula} />
    </div>
  );
}

/**
 * Motor de fluxo vertical — o formato de webtoon.
 *
 * Diferença que importa: aqui a página é UMA TIRA ALTA e o leitor rola. Cada quadro ocupa
 * aproximadamente uma tela, que é a batida de leitura do celular. A largura manda; a altura
 * é o que for preciso.
 */
function MotorFluxoVertical({ pagina, escala }: { pagina: Pagina; escala: number }) {
  return (
    <div
      className="motor-folha motor-folha-vertical"
      style={{
        width: pagina.largura,
        height: pagina.altura,
        background: pagina.corFundo ?? "var(--hq-superficie)",
        transform: `scale(${escala})`,
        transformOrigin: "top left",
      }}
    >
      <ElementosDaPagina pagina={pagina} />
      <ReticulaDaPagina reticula={pagina.reticula} />
    </div>
  );
}

/**
 * Motor SVG — vetor.
 *
 * O `viewBox` faz a página escalar para qualquer tamanho sem perder nitidez, o que é o que uma
 * capa e uma exportação precisam. Texto entra em `foreignObject` para continuar sendo texto de
 * verdade — quebrar linha em SVG puro exigiria calcular a quebra à mão, e o resultado seria
 * texto que não se copia.
 */
function MotorSvg({ pagina, escala }: { pagina: Pagina; escala: number }) {
  return (
    <svg
      className="motor-folha"
      viewBox={`0 0 ${pagina.largura} ${pagina.altura}`}
      width={pagina.largura * escala}
      height={pagina.altura * escala}
      role="img"
      aria-label={`Página ${pagina.id}`}
      style={{
        background: pagina.corFundo ?? "var(--hq-superficie)",
        // De onde o `currentColor` do ponto da trama vem. Ver o comentario no `<pattern>`.
        color: "var(--hq-halftone)",
      }}
    >
      {/* ══ A RETÍCULA EM VETOR, E POR QUE NÃO DÁ PARA REUSAR A DE CSS AQUI ═══════════════════
          Este motor já desenha tudo dentro de um `foreignObject`, então um `<div>` com
          `radial-gradient` FUNCIONARIA na tela. E seria errado: o SVG existe para capa e
          EXPORTAÇÃO, e nem `foreignObject` nem gradiente de CSS sobrevivem a um arquivo `.svg`
          aberto fora do navegador. Retícula que some na exportação é retícula que não existe
          justamente onde este motor é usado.

          `<pattern>` com `<circle>` é geometria de verdade: escala com o `viewBox` e viaja no
          arquivo. Os números são os mesmos de `TRAMA`, que o motor de CSS também lê. */}
      {OPACIDADE_RETICULA[pagina.reticula ?? "nenhuma"] > 0 && (
        <defs>
          <pattern
            id={`trama-${pagina.id}`}
            width={TRAMA.PASSO}
            height={TRAMA.PASSO}
            patternUnits="userSpaceOnUse"
          >
            {/* `currentColor` e nao `var(--hq-halftone)` direto.

                Um `<pattern>` mora em `<defs>`, e uma variavel de CSS ali depende da cascata
                alcançar o `<defs>` — o que funciona na tela e nao funciona num arquivo `.svg`
                aberto fora do navegador, que e exatamente o caso de uso deste motor. Com
                `currentColor`, a cor desce do `color` do proprio `<svg>`, que e um atributo do
                elemento e viaja com ele.

                O QUE CONTINUA EM ABERTO, e fica dito em vez de silencioso: a exportacao para
                `.svg` NAO EXISTE ainda neste produto (o que existe e `exportToPdf`, por jsPDF).
                Quando existir, quem a escrever precisa resolver o token para um literal na
                serializacao. `currentColor` deixa isso a um passo; `var()` deixaria a dois. */}
            <circle
              cx={TRAMA.PASSO / 2}
              cy={TRAMA.PASSO / 2}
              r={TRAMA.RAIO}
              fill="currentColor"
            />
          </pattern>
        </defs>
      )}

      <foreignObject x={0} y={0} width={pagina.largura} height={pagina.altura}>
        <div
          // O namespace é obrigatório dentro de foreignObject: sem ele o HTML não renderiza,
          // e falha em silêncio — o SVG aparece vazio sem erro no console.
          {...{ xmlns: "http://www.w3.org/1999/xhtml" }}
          style={{ width: pagina.largura, height: pagina.altura, position: "relative" }}
        >
          <ElementosDaPagina pagina={pagina} />
        </div>
      </foreignObject>

      {/* DEPOIS do `foreignObject`, e não antes: a retícula é acabamento e fica ACIMA dos
          elementos — é o que faz a página parecer uma coisa só quando cada imagem gerada sai com
          um tratamento diferente. E `pointer-events: none` pelo mesmo motivo do motor de CSS:
          ela não é alvo de nada. */}
      {OPACIDADE_RETICULA[pagina.reticula ?? "nenhuma"] > 0 && (
        <rect
          x={0}
          y={0}
          width={pagina.largura}
          height={pagina.altura}
          fill={`url(#trama-${pagina.id})`}
          opacity={OPACIDADE_RETICULA[pagina.reticula ?? "nenhuma"]}
          pointerEvents="none"
          aria-hidden="true"
        />
      )}
    </svg>
  );
}

/**
 * A largura que a moldura REALMENTE tem, medida na tela.
 *
 * ── O DEFEITO QUE ISTO CONSERTA ───────────────────────────────────────────────────────────────
 *
 * `larguraDisponivel` chegava aqui como um número CRAVADO — `860` nas duas telas que usam este
 * componente. Numa janela de 405px isso significa desenhar uma folha de 860px dentro de uma caixa
 * de 305px, e a `.motor-moldura` tem `overflow: hidden`.
 *
 * `overflow: hidden` corta E desliga a rolagem. Medido por validação independente: em `/motores` a
 * 405px, a folha chegava a 910px de x e **935px da página de quadrinho ficavam inalcançáveis** —
 * sem barra, sem arrasto, sem nada. É o mesmo defeito da barra de ferramentas do editor, no eixo
 * horizontal, uma rota adiante.
 *
 * E o mais instrutivo: a métrica que eu usei para declarar o T1.40 resolvido —
 * `scrollWidth - clientWidth` no documento — lia **zero** justamente porque o `overflow: hidden`
 * garante que ela leia zero. A métrica estava medindo a própria máscara.
 *
 * ── POR QUE MEDIR EM VEZ DE RECEBER ──────────────────────────────────────────────────────────
 *
 * Quem sabe quanto espaço existe é a caixa, não quem a chama. Um número passado de fora está
 * certo numa largura de tela e errado em todas as outras, e ninguém percebe porque quem escreve o
 * componente está sempre numa tela grande.
 *
 * `larguraDisponivel` continua existindo como TETO — serve à comparação lado a lado, onde faz
 * sentido limitar. O que muda é que ela deixa de ser a única fonte.
 */
function useLarguraDaCaixa(teto: number): [React.RefObject<HTMLDivElement>, number] {
  const caixa = useRef<HTMLDivElement>(null);
  const [largura, setLargura] = useState(teto);

  useEffect(() => {
    const el = caixa.current;
    if (!el) return;

    const medir = () => {
      const w = el.getBoundingClientRect().width;
      // Enquanto o elemento não tem largura (primeiro quadro, `display:none`), o teto vale — é
      // melhor desenhar grande demais por um instante do que desenhar com largura 0.
      if (w > 0) setLargura(Math.min(teto, w));
    };
    medir();

    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", medir);
      return () => window.removeEventListener("resize", medir);
    }
    const observador = new ResizeObserver(medir);
    observador.observe(el);
    return () => observador.disconnect();
  }, [teto]);

  return [caixa, largura];
}

export interface VisualizadorProps {
  pagina: Pagina;
  /** Largura disponível em pixels. A escala sai daqui. */
  larguraDisponivel?: number;
  /** Força um motor, ignorando o da página. Serve à comparação lado a lado. */
  forcarMotor?: Pagina["motor"];
}

/**
 * Desenha uma página com o motor que ela declara.
 *
 * A escala é calculada aqui e passada pronta: cada motor a aplica do seu jeito (transform no
 * HTML, width/height no SVG), mas nenhum decide qual é.
 */
export function VisualizadorDePagina({
  pagina,
  larguraDisponivel = 900,
  forcarMotor,
}: VisualizadorProps) {
  const motor = forcarMotor ?? pagina.motor;
  const [caixa, largura] = useLarguraDaCaixa(larguraDisponivel);
  // Nunca amplia além do tamanho real: uma página de 800px esticada para 1400 fica borrada.
  const escala = Math.min(1, largura / pagina.largura);

  const conteudo =
    motor === "fluxo-vertical" ? (
      <MotorFluxoVertical pagina={pagina} escala={escala} />
    ) : motor === "svg" ? (
      <MotorSvg pagina={pagina} escala={escala} />
    ) : (
      // `konva` cai aqui de propósito: o Konva é o motor de EDIÇÃO, e para LEITURA o HTML é
      // melhor (texto selecionável, acessível, buscável). Está declarado, não escondido.
      <MotorGradeCss pagina={pagina} escala={escala} />
    );

  // A `.motor-medida` existe para ter largura ELÁSTICA — ela é quem o ResizeObserver observa.
  // A `.motor-moldura` de dentro continua com largura FIXA, a da folha escalada, porque é ela que
  // recorta o desenho; se ela fosse a medida, o observador leria a própria saída e oscilaria.
  return (
    <div className="motor-medida" ref={caixa}>
      <div
        className="motor-moldura"
        // A moldura reserva a altura que a página ocupa DEPOIS da escala. Sem isso o layout salta
        // quando a página carrega.
        style={{ width: pagina.largura * escala, height: pagina.altura * escala }}
        data-motor={motor}
      >
        {conteudo}
      </div>
    </div>
  );
}

/** Todas as páginas de um documento, uma abaixo da outra. */
export function VisualizadorDeDocumento({
  documento,
  larguraDisponivel = 900,
  forcarMotor,
}: {
  documento: DocumentoHQ;
  larguraDisponivel?: number;
  forcarMotor?: Pagina["motor"];
}) {
  return (
    <div className="motor-documento">
      {documento.paginas.map((p, i) => (
        <figure key={p.id} className="motor-pagina">
          <VisualizadorDePagina
            pagina={p}
            larguraDisponivel={larguraDisponivel}
            forcarMotor={forcarMotor}
          />
          <figcaption>
            Página {i + 1} de {documento.paginas.length}
            {p.legenda ? ` — ${p.legenda}` : ""}
          </figcaption>
        </figure>
      ))}
    </div>
  );
}
